


































































































































import {Component, Vue} from 'vue-property-decorator';
import SubscriptionPlanDTO, {OverridesByProfileDTO, PlanComponentDTO, PlanPriceOverrideDTO} from '@/dto/subscription/SubscriptionPlanDTO';
import SubscriptionPlanService from '@/services/SubscriptionPlanService';
import PlanPricePayloadDTO from '@/dto/subscription/PlanPricePayloadDTO';
import {processError} from '@/utils/ComponentUtils';
import Notifications from '@/state/Notifications';
import Page, {EMPTY_PAGE} from '@/dto/taxation/Page';
import AgreementBriefDTO from '@/dto/subscription/AgreementBriefDTO';
import AddAgreementModal from '@/components/admin/sublime/AddAgreementModal.vue';

interface AddPriceFormState {
  priceDollars: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  loading: boolean
}

@Component
export default class SubscriptionPlanView extends Vue {
  private plan: SubscriptionPlanDTO | null = null;
  private loading = false;
  private forms: Record<number, AddPriceFormState> = {};
  private adding: Record<number, boolean> = {};

  // Agreements state
  private agreementsPage: Page<AgreementBriefDTO> = {...EMPTY_PAGE} as Page<AgreementBriefDTO>;
  private agreementsLoading = false;
  private agreementsPageSize = 20;

  private get profileOverrides(): Array<OverridesByProfileDTO> {
    return this.plan?.overridesByProfile || [];
  }

  private rowsFor(component: PlanComponentDTO): Array<{ key: string; unitPriceCents: number; effectiveFrom: string; effectiveTo?: string | null; profile?: { id: number; code: string; name: string } | null }> {
    const rows: Array<{ key: string; unitPriceCents: number; effectiveFrom: string; effectiveTo?: string | null; profile?: { id: number; code: string; name: string } | null }> = [];
    // Default prices
    (component.prices || []).forEach(p => {
      rows.push({key: `d-${component.id}-${p.id}`, unitPriceCents: p.unitPriceCents, effectiveFrom: p.effectiveFrom, effectiveTo: p.effectiveTo || null, profile: null});
    });
    // Overrides
    this.profileOverrides.forEach(prof => {
      (prof.overrides || []).forEach((ovr: PlanPriceOverrideDTO) => {
        if (ovr.planComponentId === component.id) {
          rows.push({
            key: `o-${component.id}-${prof.profileId}-${ovr.effectiveFrom}-${ovr.unitPriceCents}`,
            unitPriceCents: ovr.unitPriceCents,
            effectiveFrom: ovr.effectiveFrom,
            effectiveTo: ovr.effectiveTo || null,
            profile: {id: prof.profileId, code: prof.profileCode, name: prof.profileName},
          });
        }
      });
    });
    // Sort by effectiveFrom ASC then by profile presence (defaults first)
    rows.sort((a, b) => {
      if (a.effectiveFrom < b.effectiveFrom) return -1;
      if (a.effectiveFrom > b.effectiveFrom) return 1;
      const aProf = a.profile ? 1 : 0;
      const bProf = b.profile ? 1 : 0;
      return aProf - bProf;
    });
    return rows;
  }

  mounted(): void {
    const id = this.$route.params.id;
    this.fetchPlan(id);
  }

  private fetchPlan(id: number | string): void {
    this.loading = true;
    SubscriptionPlanService.getPlan(id).then(
        ok => {
          this.plan = ok.data;
          this.loading = false;
          this.loadAgreements(0);
        },
        err => {
          this.loading = false;
          processError(err, this);
        }
    );
  }

  private loadAgreements(pageNumber: number): void {
    if (!this.plan) return;
    this.agreementsLoading = true;
    SubscriptionPlanService.getAgreements(this.plan.id, pageNumber, this.agreementsPageSize).then(
        ok => {
          const data: any = ok.data as any;
          if (Array.isArray(data)) {
            // Backend returned a plain list; wrap into a Page shape so UI renders rows
            this.agreementsPage = {
              content: data as AgreementBriefDTO[],
              number: 0,
              totalPages: 1,
              size: (data as AgreementBriefDTO[]).length,
              first: true,
              last: true,
              empty: (data as AgreementBriefDTO[]).length === 0,
            } as Page<AgreementBriefDTO>;
          } else {
            this.agreementsPage = data as Page<AgreementBriefDTO>;
          }
          this.agreementsLoading = false;
        },
        err => {
          this.agreementsLoading = false;
          processError(err, this);
        }
    );
  }

  private changeAgreementsPage(page: number): void {
    this.loadAgreements(page - 1);
  }

  private openAddAgreement(): void {
    if (!this.plan) return;
    this.$modal.show(
        AddAgreementModal,
        {
          planId: this.plan.id,
          issuer: this.plan.issuer,
          onCreated: () => {
            this.$modal.hideAll();
            this.fetchPlan(this.plan!.id);
            this.changeAgreementsPage(1);
          }
        },
        {
          width: 900
        }
    )
  }

  private formFor(componentId: number): AddPriceFormState {
    if (!this.forms[componentId]) {
      this.$set(this.forms, componentId, {priceDollars: '', effectiveFrom: '', effectiveTo: null, loading: false});
    }
    return this.forms[componentId];
  }

  private isAdding(componentId: number): boolean {
    return !!this.adding[componentId];
  }

  private startAdding(componentId: number): void {
    this.$set(this.adding, componentId, true);
  }

  private cancelAdding(componentId: number): void {
    const form = this.formFor(componentId);
    form.priceDollars = '';
    form.effectiveFrom = '';
    form.effectiveTo = null;
    this.$set(this.adding, componentId, false);
  }

  private confirmAdd(componentId: number, componentCode: string): void {
    this.addPrice(componentId, componentCode);
  }

  private addPrice(componentId: number, componentCode: string): void {
    if (!this.plan) return;
    const form = this.formFor(componentId);
    const price = Number(form.priceDollars);
    if (!form.effectiveFrom || Number.isNaN(price) || price < 0) {
      Notifications.warning('Please provide valid Unit price and Effective from date');
      return;
    }
    const payload: PlanPricePayloadDTO = {
      unitPriceCents: Math.round(price * 100),
      effectiveFrom: form.effectiveFrom,
      effectiveTo: form.effectiveTo || null,
    };
    form.loading = true;
    SubscriptionPlanService.addPrice(this.plan.id, componentCode, payload).then(
        ok => {
          // Reload the plan from backend to reflect all server-side changes
          this.fetchPlan(this.plan!.id);
          form.priceDollars = '';
          form.effectiveFrom = '';
          form.effectiveTo = null;
          form.loading = false;
          this.$set(this.adding, componentId, false);
        },
        err => {
          form.loading = false;
          processError(err, this);
        }
    );
  }

  private formatCents(cents?: number | null): string {
    if (cents === null || cents === undefined) return '';
    return `$${(cents / 100).toFixed(2)}`;
  }

  private displayIssuer(issuer: any): string {
    if (issuer && typeof issuer === 'object') {
      return issuer.fullName || issuer.abbreviation || JSON.stringify(issuer);
    }
    if (typeof issuer === 'string') {
      return this.$stringUtils.enumToFormattedSentence(issuer);
    }
    return String(issuer);
  }
}
